import {
  timezoneData,
  useTranslation,
  zonedToUTC,
  zonedToUTCAdd1Day
} from '@fareye/common'
import {
  AsyncSearchSelect,
  components,
  CreatableSearchSelect,
  Flex,
  Label,
  SearchSelect
} from '@fareye/ui'
import {
  cancelToken,
  FaCaretDown,
  FaCaretUp,
  get,
  getQueryParams,
  yyyyMmDd,
  isEmpty,
  isEqual,
  search,
  startCase
} from '@fareye/utils'
import debounce from 'debounce-promise'
import { ErrorMessage } from 'formik'
import React from 'react'
import { Column, ErrorSpan } from '../styled/styled'
import { getDescendantProp } from '../utility/funcs'
import { IComponentFormField, IFormRowConfig } from '../utility/types'
import { CustomErrorMessage } from './CustomErrorMessage'
import { CustomLabel } from './Label'

function SelectBox({
  config,
  setFieldValue,
  setFieldTouched,
  formKeyName,
  values,
  baseUrl,
  showTimezone
}: IComponentFormField) {
  const {
    styling,
    apiUrl,
    configQuery = '',
    searchQueryKey,
    unit,
    apiResultKey,
    creatableType
  } = config
  const [optionsLocal, setOptionsLocal] = React.useState<any[]>([])
  const source = cancelToken().source()
  //To display number of Records
  const recordsToShow = 30
  let flag = 0

  React.useEffect(() => {
    let isCancelled = false
    if (config.type === 'local-select') {
      fetchLocalData(isCancelled)
    }
    return () => {
      isCancelled = true
      source.cancel('Unmount api')
    }
  }, [])
  const { t } = useTranslation()

  const loadOptions = async (value: string, isFirstTime?: boolean) => {
    try {
      const startDate = values?.['dateRange']?.['startDate']
      const endDate = values?.['dateRange']?.['endDate']
      const apiBaseUrl = baseUrl + apiUrl
      const query = getQueryParams(
        {
          [searchQueryKey ? searchQueryKey : 'search']: value,
          startDate: startDate
            ? showTimezone
              ? zonedToUTC(new Date(yyyyMmDd(startDate as Date)))
              : new Date(yyyyMmDd(startDate as Date))
            : null,
          endDate: endDate
            ? showTimezone
              ? zonedToUTCAdd1Day(new Date(yyyyMmDd(endDate as Date)))
              : new Date(yyyyMmDd(endDate as Date))
            : null
        },
        showTimezone
      )
      const data = await search(`${apiBaseUrl}?${query}${configQuery}`)
      const { [apiResultKey as string]: apiData } = data
      return closest(apiData, value).filter((item: any) => item.label !== '')
    } catch (ex:any) {}
  }

  const Option = (props: any) => {
    return (
      <div>
        <components.Option {...props}>
          <div
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between'
            }}
          >
            <span>{t(props.data.label)}</span>
            <Label>
              {t(startCase(props?.data?.pretty_name || props?.data?.key))}
            </Label>
          </div>
        </components.Option>
      </div>
    )
  }
  const DropdownIndicator = (props: any) => {
    const {
      selectProps: { menuIsOpen }
    } = props
    return (
      <components.DropdownIndicator {...props}>
        {menuIsOpen ? <FaCaretDown /> : <FaCaretUp />}
      </components.DropdownIndicator>
    )
  }
  const closest = (arr: any, searchValue: string) => {
    return arr.sort((a: any, b: any) => {
      const diff1 = a.value.toLowerCase().includes(searchValue.toLowerCase())
        ? searchValue.length / a.value.length
        : 0
      const diff2 = b.value.toLowerCase().includes(searchValue.toLowerCase())
        ? searchValue.length / b.value.length
        : 0
      return diff1 < diff2 ? 1 : -1
    })
  }

  async function fetchLocalData(isCancelled: boolean) {
    const { apiUrl, searchQueryKey,labelForDisplay,valueForDisplay } = config
    const apiBaseUrl = baseUrl + apiUrl
    try {
      const { data } = await get(apiBaseUrl, source.token)
      let options = getDescendantProp(
        data,
        searchQueryKey ? searchQueryKey : 'results'
      )
      if (!Array.isArray(options)) {
        options = Object.keys(options).map(ele => {
          return {
            label: ele,
            options: options[ele]
          }
        })
      } else if (typeof options[0] === 'string') {
        options = options.map(ele => {
          return {
            label: ele,
            value: ele
          }
        })
      }
      if (!isCancelled) {
        setOptionsLocal(options)
      }
    } catch (ex:any) {
      console.warn('Error while fetching dropdown values')
    }
  }
  function onCreateOption(
    label: string,
    isMultiFlag?: boolean,
    selectLimit?: number
  ) {
    let selected = null
    if (creatableType && creatableType === 'number') {
      let isValidLabel = !isNaN(Number(label))
      if (isValidLabel) {
        selected = {
          value: Number(label),
          label: label + (unit ? ' ' + unit : '')
        }
      }
    } else {
      selected = {
        value: label,
        label: label + (unit ? ' ' + unit : '')
      }
    }

    if (selected) {
      let previousValue = getDescendantProp(values, formKeyName)
      previousValue = previousValue ? previousValue : []
      let updatedValue = null
      if (isMultiFlag) {
        if (
          selectLimit &&
          (previousValue as Array<any>).length >= selectLimit
        ) {
          updatedValue = [...previousValue]
        } else {
          updatedValue = [...previousValue, selected]
        }
      } else {
        updatedValue = selected
      }
      setFieldValue(formKeyName, updatedValue)
    }
  }

  function onChangeInCreatableSearch(
    value?: any,
    isMultiFlag?: boolean,
    selectLimit?: number
  ) {
    let isSetFieldValue = !(
      isMultiFlag &&
      selectLimit &&
      value &&
      (value as Array<any>).length > selectLimit
    )

    if (isSetFieldValue) {
      setFieldValue(formKeyName, value)
    }
  }

  const debounceLoadOption = debounce(loadOptions, 400)

  const poisDefaultCache = React.useRef<any[]>([])

  const loadPoisOptions = async (inputValue: string) => {
    try {
      if (inputValue && inputValue.length > 0 && inputValue.length < 3) {
        return poisDefaultCache.current
      }
      const apiBaseUrl = baseUrl + apiUrl
      const url =
        inputValue && inputValue.length >= 3
          ? `${apiBaseUrl}?search=${encodeURIComponent(inputValue)}&limit=100`
          : `${apiBaseUrl}?limit=100`
      const { data } = await get(url, source.token)
      const results = data?.results || []
      if (!inputValue || inputValue.length === 0) {
        poisDefaultCache.current = results
      }
      return results
    } catch (ex: any) {
      return []
    }
  }

  const debouncedLoadPoisOptions = debounce(loadPoisOptions, 300)

  function getSearchSelectComponent() {
    const {
      options,
      isMulti,
      multiSelectLimit,
      placeholder,
      labelForDisplay,
      valueForDisplay
    } = config
    const optionsIsEmpty = isEmpty(optionsLocal) && isEmpty(options)
    if (optionsIsEmpty && config.formKey == 'user_timezone') {
      config.options = timezoneData as IFormRowConfig
    }
    const translatedPlaceholder = placeholder ? t(placeholder.trim()) : ''
    switch (config.type) {
      case 'creatable':
        return (
          <CreatableSearchSelect
            options={options as any}
            isMulti={isMulti}
            onBlur={() => {
              setFieldTouched(formKeyName, true)
            }}
            value={getDescendantProp(values, formKeyName)}
            onChange={value => {
              onChangeInCreatableSearch(value, isMulti, multiSelectLimit)
            }}
            components={{ DropdownIndicator }}
            placeholder={translatedPlaceholder}
            getOptionLabel={ele =>
              labelForDisplay ? ele[labelForDisplay] : ele.label
            }
            styles={{
              container: (base: any) => ({
                ...base,
                flexGrow: 1
              }),
              control: (base: any) => ({
                ...base,

                borderColor: '#b3b3b3',
                boxShadow: 'none'
              }),
              option: (base: any) => ({
                ...base,
                display: 'flex',
                justifyContent: 'space-between',
                zIndex: 10
              })
            }}
            onCreateOption={input =>
              onCreateOption(input, isMulti, multiSelectLimit)
            }
            getOptionValue={ele =>
              valueForDisplay ? ele[valueForDisplay] : ele.value
            }
          />
        )
      case 'async':
        return (
          <AsyncSearchSelect
            placeholder={translatedPlaceholder}
            value={getDescendantProp(values, formKeyName)}
            loadOptions={(value: string) => debounceLoadOption(value)}
            onBlur={() => {
              setFieldTouched(formKeyName, true)
            }}
            components={{
              Option,
              IndicatorSeparator: () => null,
              DropdownIndicator
            }}
            onChange={value => {
              setFieldValue(formKeyName, value)
            }}
            styles={{
              container: (base: any) => ({
                ...base,
                flexGrow: 1
              }),
              control: (base: any) => ({
                ...base,
                borderColor: '#b3b3b3',
                boxShadow: 'none'
              }),
              option: (base: any) => ({
                ...base,
                display: 'flex',
                justifyContent: 'space-between',
                zIndex: 10
              })
            }}
            isMulti
            getOptionLabel={ele =>
              labelForDisplay ? ele[labelForDisplay] : ele.label
            }
            getOptionValue={ele =>
              valueForDisplay ? ele[valueForDisplay] : ele.value
            }
            noOptionsMessage={() => `${t('Type to Filter Data')} ..`}
          />
        )
      case 'poi-async-select':
        return (
          <AsyncSearchSelect
            placeholder={translatedPlaceholder}
            value={getDescendantProp(values, formKeyName)}
            defaultOptions
            loadOptions={(value: string) => debouncedLoadPoisOptions(value)}
            onBlur={() => setFieldTouched(formKeyName, true)}
            onChange={value => setFieldValue(formKeyName, value)}
            components={{ DropdownIndicator }}
            styles={{
              container: (base: any) => ({ ...base, flexGrow: 1 }),
              control: (base: any) => ({
                ...base,
                borderColor: '#b3b3b3',
                boxShadow: 'none'
              }),
              option: (base: any) => ({
                ...base,
                display: 'flex',
                justifyContent: 'space-between',
                zIndex: 10
              })
            }}
            isMulti={isMulti}
            getOptionLabel={(ele: any) => {
              const label = labelForDisplay && ele[labelForDisplay]
              if (label) return t(label)
              const fallback = valueForDisplay && ele[valueForDisplay]
              return fallback ? String(fallback) : t(ele.label || '')
            }}
            getOptionValue={(ele: any) =>
              valueForDisplay ? ele[valueForDisplay] : ele.value
            }
            noOptionsMessage={() => t('No POIs found')}
          />
        )
      case 'local-select':
        return (
          <SearchSelect
            valueForDisplay={valueForDisplay}
            options={optionsLocal as any}
            value={getDescendantProp(values, formKeyName)}
            onChange={value => {
              setFieldValue(formKeyName, value)
            }}
            placeholder={translatedPlaceholder}
            onBlur={() => {
              setFieldTouched(formKeyName, true)
            }}
            components={{ DropdownIndicator }}
            t={t}
            filterOption={({ label }, query) =>
              label
                ? label.toLowerCase().indexOf(query.toLowerCase()) >= 0 &&
                  flag + 1 < recordsToShow
                : false
            }
            //reset flag after each input
            onInputChange={() => {
              flag = 0
            }}
            styles={{
              container: (base: any) => ({
                ...base,
                flexGrow: 1
              }),
              control: (base: any) => ({
                ...base,
                borderColor: '#b3b3b3',
                boxShadow: 'none'
              }),
              option: (base: any) => ({
                ...base,
                display: 'flex',
                justifyContent: 'space-between',
                zIndex: 10
              })
            }}
            isMulti={isMulti}
            getOptionLabel={ele =>
              labelForDisplay ? t(ele[labelForDisplay]) : t(ele.label)
            }
            getOptionValue={ele =>
              valueForDisplay ? ele[valueForDisplay] : ele.value
            }
          ></SearchSelect>
        )
      default:
        return (
          <SearchSelect
            options={options as any}
            value={getDescendantProp(values, formKeyName)}
            onChange={value => {
              setFieldValue(formKeyName, value)
            }}
            placeholder={translatedPlaceholder}
            onBlur={() => {
              setFieldTouched(formKeyName, true)
            }}
            components={{ DropdownIndicator }}
            t={t}
            styles={{
              container: (base: any) => ({
                ...base,
                flexGrow: 1
              }),
              control: (base: any) => ({
                ...base,
                borderColor: '#b3b3b3',
                boxShadow: 'none'
              }),
              option: (base: any) => ({
                ...base,
                display: 'flex',
                justifyContent: 'space-between',
                zIndex: 10
              })
            }}
            isMulti={isMulti}
            getOptionLabel={ele =>
              labelForDisplay ? t(ele[labelForDisplay]) : t(ele.label)
            }
            getOptionValue={ele =>
              valueForDisplay ? ele[valueForDisplay] : ele.value
            }
          ></SearchSelect>
        )
    }
  }
  const { label = {}, container = {}, inputContainer = {} } = styling
    ? styling.styles
      ? styling.styles
      : {}
    : {}
  return (
    <Column {...(styling ? styling.variant : {})} style={container} column>
      <CustomLabel
        label={config.label as string}
        desc={config.desc}
        style={label}
      />
      <Flex width={1} column style={inputContainer}>
        {getSearchSelectComponent()}
        <ErrorSpan variant="danger">
          <ErrorMessage
            component={(props: any) => <CustomErrorMessage {...props} />}
            name={formKeyName}
          />
        </ErrorSpan>
      </Flex>
    </Column>
  )
}
export const Select = React.memo(
  SelectBox,
  (oldProp: IComponentFormField, newProp: IComponentFormField) => {
    return isEqual(oldProp, newProp)
  }
)
